import { t } from 'i18next';

import { BomPartType } from 'bom/enums';
import { DEFAULT_PLACEHOLDER, NOT_AVAILABLE } from 'core/constants';
import { ICorporateLocation } from 'corporate/types';
import { CostSourceHelper, PriceSourceHelper } from 'pricing/helpers';
import { QuotePartInlineActionDrawer } from 'quotes/components/QuotePartList';
import { QuoteVersionType } from 'quotes/enums';
import { ICustomerQuotePart } from 'quotes/types';
import { isBaseAdderPnbPricingEnabled, isCostPnbFixed } from 'quotes/utils';

export const isEditable = ({
  data,
  currentQuoteVersion,
}: {
  data: ICustomerQuotePart;
  currentQuoteVersion: QuoteVersionType;
}) => {
  if (!data) {
    return false;
  }

  return (
    QuoteVersionType.DRAFT === currentQuoteVersion &&
    BomPartType.DISCOUNT !== data.type &&
    !data.substituted &&
    !data.noQuote &&
    !data.locked &&
    !data.deleted
  );
};

export const getCostCellValue = (quotePart: ICustomerQuotePart) => {
  // If no quotePart, means that the grouping by another column is applied, display no value
  if (!quotePart) {
    return '';
  }

  if (!quotePart?.costSourceId) {
    return t('messageNoCost');
  }

  const costSourceLabel = CostSourceHelper.getTypeLabel(quotePart);
  const costSourceDiscountTypeLabel = CostSourceHelper.getDiscountTypeLabel(quotePart);
  const pricingProfileName = CostSourceHelper.getPricingProfileName(quotePart);

  return [
    costSourceLabel.long,
    costSourceDiscountTypeLabel.long,
    pricingProfileName,
    quotePart?.costSourceDiscountValue,
  ];
};

export const getPriceCellValue = (quotePart: ICustomerQuotePart) => {
  // If no quotePart, means that the grouping by another column is applied, display no value
  if (!quotePart) {
    return '';
  }

  if (!quotePart?.priceSourceId) {
    return t('messageNoPrice');
  }

  const priceSourceLabel = PriceSourceHelper.getTypeLabel(quotePart);
  const priceSourceDiscountTypeLabel = PriceSourceHelper.getDiscountTypeLabel(quotePart);
  const pricingProfileName = PriceSourceHelper.getPricingProfileName(quotePart);
  const quantityDiscountsApplied = PriceSourceHelper.getQuantityDiscountsApplied(quotePart);

  return [
    priceSourceLabel,
    priceSourceDiscountTypeLabel,
    quantityDiscountsApplied,
    pricingProfileName,
  ];
};

export const getVendorCellValue = (quotePart: ICustomerQuotePart): string | undefined => {
  const { companyManufacturerName, companyManufacturerId, accountsPayableId } = quotePart || {};

  if (!companyManufacturerName && !companyManufacturerId && !accountsPayableId) {
    return;
  }

  return `${companyManufacturerName} ${companyManufacturerId} ${accountsPayableId}`;
};

export const onMarginClick = (
  quotePart: ICustomerQuotePart,
  openDrawer: (drawer: QuotePartInlineActionDrawer) => void
) =>
  openDrawer(
    isBaseAdderPnbPricingEnabled(quotePart) && !isCostPnbFixed(quotePart)
      ? QuotePartInlineActionDrawer.UPDATE_PNB_MARGIN
      : QuotePartInlineActionDrawer.UPDATE_MARGIN
  );

export const getShippingLocationLabel = (
  corporateLocation: ICorporateLocation,
  isAlternateVersionEnabled = false
) => {
  if (!corporateLocation) {
    return DEFAULT_PLACEHOLDER;
  }

  const vendorId = isAlternateVersionEnabled
    ? corporateLocation.externalLocationId
    : corporateLocation.vendorIdentifier;

  return `${corporateLocation.addressName || NOT_AVAILABLE} (${vendorId})`;
};
